/* ============================================================
   ÉKO '77 — quick buy, on the Shopify Cart AJAX API.

   The port of the reference assets/js/quickbuy.js. Its opening comment
   states the rule the whole feature exists for:

     "a dish has sizes, so it must never be added silently at whatever
      the cheapest one happens to be"

   so the + on a tile does NOT add anything. It opens the panel, the size
   is chosen there, and only then does it reach the basket. Everything
   else — 1..99 quantity clamp, the live "Add to basket · £x" total, the
   "Feeds around N people" line, Escape, the focus trap, the scrim, the
   scroll lock, the 280ms close — is the reference's behaviour, kept.

   WHAT CHANGED IN THE PORT
     · dish data           products.json          -> sections/x-quick-buy.liquid
                                                     through the Section
                                                     Rendering API, because
                                                     `serves` is a VARIANT
                                                     metafield and money must
                                                     stay in the shop format
     · sizes               d.sizes[]              -> product.variants[]
     · add to basket       window.__addLine()     -> POST /cart/add.js
     · basket refresh      render() on a JS array -> the theme: pub/sub
                                                     `cart-update` +
                                                     `eko:cart:updated`, which
                                                     assets/x-cartbar.js and the
                                                     cart drawer both listen for

   DELEGATION: the click handler is on `document`, like assets/x-search.js,
   so it does not care whether the tiles were server-rendered, appended by
   the load-more pager, swapped by the category chips, or re-rendered by
   the theme editor.
   ============================================================ */

(function () {
  'use strict';

  var SECTION_ID = 'x-quick-buy';
  var CLOSE_MS = 280;
  var DONE_MS = 1400;
  var TOAST_MS = 2100;
  var MAX_QTY = 99;

  var root = null;
  var panel = null;
  var content = null;
  var toastEl = null;
  var toastTimer = null;

  var cache = {};
  var lastFocus = null;
  var originBtn = null;
  var state = { url: '', title: '', qty: 1, size: null, moneyFormat: '' };

  /* ---------- money -------------------------------------------------
     Liquid paints quantity 1, so this only ever runs from 2 up. Seeded
     from shop.money_format, so it cannot drift from the rest of the site.
     Canonical Shopify formatMoney, then the same trailing-zero trim the
     Liquid side does with `money_without_trailing_zeros` — otherwise the
     total jumps from "£30" at qty 1 to "£60.00" at qty 2 in one panel.
     ---------------------------------------------------------------- */
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    thousands = thousands === undefined ? ',' : thousands;
    decimal = decimal === undefined ? '.' : decimal;
    if (isNaN(cents) || cents === null) return '0';
    var number = (cents / 100.0).toFixed(precision);
    var parts = number.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function money(cents) {
    var format = state.moneyFormat || '${{amount}}';
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;
    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* Whole amounts lose the ",00" / ".00" group; anything with real pence
     keeps it. Mirrors the Liquid filter rather than guessing at the
     separator, so a store on 1.234,00 formats trims correctly too. */
  function moneyTrimmed(cents) {
    var out = money(cents);
    if (cents % 100 !== 0) return out;
    return out.replace(/([.,])0{2}(?!\d)/, '');
  }

  /* ---------- toast ------------------------------------------------- */
  function toast(message) {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'x-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-on');
    }, TOAST_MS);
  }

  /* ---------- the shell, built once --------------------------------- */
  function build() {
    root = document.createElement('div');
    root.className = 'x-qb';
    root.hidden = true;
    root.innerHTML =
      // a div, like the reference: a <button> here would carry UA styling and
      // a second, unlabelled "close" into the a11y tree. Esc and the X button
      // are the keyboard routes out; the scrim is pointer-only by design.
      '<div class="x-qb__scrim" data-x-qb-close></div>' +
      '<div class="x-qb__panel" role="dialog" aria-modal="true" aria-labelledby="x-qb-name">' +
      '<button class="x-qb__x" type="button" data-x-qb-close>' +
      '<span class="visually-hidden" data-x-qb-close-label></span>' +
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M5 5l14 14M19 5 5 19" /></svg></button>' +
      '<div class="x-qb__content" data-x-qb-content></div>' +
      '</div>';
    document.body.appendChild(root);

    panel = root.querySelector('.x-qb__panel');
    content = root.querySelector('[data-x-qb-content]');

    root.addEventListener('click', onPanelClick);
    document.addEventListener('keydown', onKeydown);
  }

  function onPanelClick(event) {
    if (event.target.closest('[data-x-qb-close]')) {
      close();
      return;
    }

    var step = event.target.closest('[data-x-qb-step]');
    if (step) {
      setQty(state.qty + parseInt(step.dataset.xQbStep, 10));
      return;
    }

    var size = event.target.closest('[data-x-qb-size]');
    if (size && !size.disabled) {
      state.size = size;
      state.qty = 1;
      paint();
      return;
    }

    if (event.target.closest('[data-x-qb-add]')) add();
  }

  function onKeydown(event) {
    if (!root || root.hidden) return;
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
    if (event.key === 'Tab') trap(event);
  }

  /* the reference's trap, minus its bug: it queried every button and
     anchor including hidden and disabled ones, so Tab could land on a
     sold-out size and appear to do nothing */
  function trap(event) {
    var focusable = Array.prototype.filter.call(
      panel.querySelectorAll('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) {
        return !el.disabled && el.offsetParent !== null && el.getAttribute('tabindex') !== '-1';
      }
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function setQty(next) {
    state.qty = Math.min(MAX_QTY, Math.max(1, next || 1));
    paint();
  }

  /* ---------- paint -------------------------------------------------- */
  function paint() {
    if (!content) return;

    var qtyEl = content.querySelector('[data-x-qb-qty]');
    var addEl = content.querySelector('[data-x-qb-add]');
    var servesEl = content.querySelector('[data-x-qb-serves]');

    if (qtyEl) qtyEl.textContent = String(state.qty);

    content.querySelectorAll('[data-x-qb-size]').forEach(function (btn) {
      var on = btn === state.size;
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-pressed', String(on));
    });

    if (addEl && state.size && !addEl.disabled) {
      var template = addEl.dataset.template || '[total]';
      var total =
        state.qty === 1
          ? state.size.dataset.priceMoney
          : moneyTrimmed(parseInt(state.size.dataset.price, 10) * state.qty);
      addEl.textContent = template.replace('[total]', total);
    }

    if (servesEl) {
      var serves = state.size ? parseInt(state.size.dataset.serves, 10) : 0;
      servesEl.textContent =
        serves > 0 ? (servesEl.dataset.template || '[people]').replace('[people]', String(serves * state.qty)) : '';
    }
  }

  /* ---------- open / close ------------------------------------------- */
  function open(url, title, button) {
    if (!root) build();

    originBtn = button || null;
    state.url = url;
    state.title = title || '';
    state.qty = 1;
    state.size = null;

    lastFocus = document.activeElement;
    root.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () {
      root.classList.add('is-on');
    });

    if (cache[url]) {
      mount(cache[url].cloneNode(true));
      return;
    }

    content.innerHTML = '<p class="x-qb__loading">' + (root.dataset.loading || '') + '</p>';

    var endpoint = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + SECTION_ID;

    fetch(endpoint)
      .then(function (response) {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then(function (html) {
        var node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-x-qb-render]');
        if (!node) throw new Error('no panel');
        cache[url] = node.cloneNode(true);
        // a second click while the first was still in flight already closed it
        if (state.url === url) mount(node);
      })
      .catch(function () {
        // never strand the shopper in an empty dialog — the tile is a link
        close();
        window.location.href = url;
      });
  }

  function mount(node) {
    content.innerHTML = '';
    content.appendChild(node);

    state.moneyFormat = node.dataset.moneyFormat || state.moneyFormat;
    state.title = node.dataset.title || state.title;

    var sizes = content.querySelectorAll('[data-x-qb-size]:not([disabled])');
    state.size = sizes.length ? sizes[0] : content.querySelector('[data-x-qb-size]');
    state.qty = 1;
    paint();

    var closeBtn = root.querySelector('.x-qb__x');
    var closeLabel = root.querySelector('[data-x-qb-close-label]');
    if (closeLabel && root.dataset.close) {
      closeLabel.textContent = root.dataset.close;
      closeBtn.setAttribute('aria-label', root.dataset.close);
    }

    // the merchant-editable terms line, taken from x-shop-grid's setting
    var termsEl = content.querySelector('[data-x-qb-terms]');
    var override = document.querySelector('[data-x-quick-buy-terms]');
    if (termsEl && override) termsEl.textContent = override.dataset.xQuickBuyTerms;

    setTimeout(function () {
      closeBtn.focus();
    }, 60);
  }

  function close() {
    if (!root || root.hidden) return;
    state.url = '';
    root.classList.remove('is-on');
    document.body.style.overflow = '';
    setTimeout(function () {
      root.hidden = true;
    }, CLOSE_MS);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ---------- add to the basket -------------------------------------- */
  function add() {
    if (!state.size) return;

    var addBtn = content.querySelector('[data-x-qb-add]');
    if (!addBtn || addBtn.disabled) return;

    var variantId = state.size.dataset.variantId;
    var qty = state.qty;
    var label = addBtn.textContent;

    addBtn.disabled = true;

    var cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    fetch(cartAddUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      })
      .then(function (data) {
        var line = (data.items && data.items[0]) || {};
        var name = line.product_title
          ? line.product_title + (line.variant_title ? ' — ' + line.variant_title : '')
          : state.title;

        announce(name, qty);
        flashOrigin();
        notifyCart();
        close();
      })
      .catch(function (error) {
        // Shopify puts the human-readable reason in `description`
        toast((error && (error.description || error.message)) || (root && root.dataset.error) || '');
        addBtn.disabled = false;
        addBtn.textContent = label;
      });
  }

  function announce(name, qty) {
    var template = (root && root.dataset.added) || '';
    if (!template) return;
    toast(template.replace('[title]', name).replace('[quantity]', String(qty)));
  }

  function flashOrigin() {
    if (!originBtn) return;
    var btn = originBtn;
    btn.classList.add('is-done');
    setTimeout(function () {
      btn.classList.remove('is-done');
    }, DONE_MS);
  }

  /* the cart bar, the cart drawer and anything else already listening —
     see the contract in assets/x-cartbar.js */
  function notifyCart() {
    document.dispatchEvent(new CustomEvent('eko:cart:updated', { detail: { source: 'x-quick-buy' } }));
    if (typeof window.publish === 'function') {
      window.publish('cart-update', { source: 'x-quick-buy' });
    }
  }

  /* ---------- the tiles ---------------------------------------------- */
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-x-quick-buy]');
    if (!btn) return;

    // the rest of the tile is a link to the dish; the button is not
    event.preventDefault();
    event.stopPropagation();
    if (btn.disabled) return;

    open(btn.dataset.productUrl, btn.dataset.productTitle, btn);
    btn.blur();
  });

  /* strings live in Liquid, so they arrive on a data island that
     x-shop-grid (or any section rendering the tile) prints once */
  function adopt() {
    var island = document.querySelector('[data-x-quick-buy-strings]');
    if (!island || !root) return;
    root.dataset.close = island.dataset.close || '';
    root.dataset.added = island.dataset.added || '';
    root.dataset.error = island.dataset.error || '';
    root.dataset.loading = island.dataset.loading || '';
    state.moneyFormat = island.dataset.moneyFormat || state.moneyFormat;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      build();
      adopt();
    });
  } else {
    build();
    adopt();
  }
})();
